import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import authService from '../../services/authService';
import { navigateAfterAuth } from '../../utils/postLoginNavigation';
import AuthPageLayout from '../AuthPageLayout/AuthPageLayout';
import './PartnerSetup.css';

const SUPPLIER_ROLE_ID = 11;
const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

const SupplierSetup: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.user);
  const [legalName, setLegalName] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [gstin, setGstin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const tradeNameManuallyEdited = useRef(false);

  useEffect(() => {
    const finishInit = async () => {
      try {
        const response = await authService.getCurrentUser();
        if (!response.success || !response.user) {
          navigate('/signin?redirect=%2Fpartner%2Fsetup%2Fsupplier');
          return;
        }

        const redirectUrl = response.redirectUrl || '/';
        if (redirectUrl === '/supplier/dashboard' || response.user.roleId === SUPPLIER_ROLE_ID) {
          navigate('/supplier/dashboard', { replace: true });
          return;
        }
      } catch {
        navigate('/signin?redirect=%2Fpartner%2Fsetup%2Fsupplier');
        return;
      } finally {
        setInitializing(false);
      }
    };

    if (!user) {
      void finishInit();
      return;
    }

    void (async () => {
      try {
        const response = await authService.getCurrentUser();
        const redirectUrl = response.redirectUrl || '/';
        if (response.success && redirectUrl === '/supplier/dashboard') {
          await navigateAfterAuth(redirectUrl, { isPartners: true, navigate });
          return;
        }
      } catch {
        // fall through to form
      }
      setInitializing(false);
    })();
  }, [user, navigate]);

  const handleLegalNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLegalName(value);
    if (!tradeNameManuallyEdited.current) {
      setTradeName(value);
    }
  };

  const handleTradeNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    tradeNameManuallyEdited.current = true;
    setTradeName(e.target.value);
  };

  const handleGstinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGstin(e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 15));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedLegal = legalName.trim();
    const trimmedTrade = tradeName.trim();
    const trimmedGstin = gstin.trim();

    if (!trimmedLegal) {
      setError('Legal name is required.');
      return;
    }
    if (trimmedGstin && !GSTIN_PATTERN.test(trimmedGstin)) {
      setError('Enter a valid 15-character GSTIN or leave it blank.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/partner/setup/saveSupplierBusiness', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          legalName: trimmedLegal,
          tradeName: trimmedTrade || trimmedLegal,
          gstin: trimmedGstin || undefined,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setError(data.message || 'Unable to save your business details. Please try again.');
        return;
      }
      navigate(data.redirectUrl || '/supplier/dashboard', { replace: true });
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (initializing) {
    return (
      <AuthPageLayout>
        <div className="partner-setup-container">
          <div className="partner-setup-wrapper">
            <div className="partner-setup-form">
              <p className="partner-setup-subtitle">Loading...</p>
            </div>
          </div>
        </div>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout>
      <div className="partner-setup-container">
        <div className="partner-setup-wrapper">
          <div className="partner-setup-form">
            <h2 className="partner-setup-title">Complete Your Supplier Setup</h2>
            <p className="partner-setup-subtitle">
              Tell us about your business to access the supplier portal
            </p>

            {error && <div className="error-message" role="alert">{error}</div>}

            <div className="step-content active">
              <h3>Business Details</h3>
              <form onSubmit={handleSubmit}>
                <div className="input-group-wrapper">
                  <label htmlFor="legalName">Legal Name *</label>
                  <input
                    type="text"
                    id="legalName"
                    value={legalName}
                    onChange={handleLegalNameChange}
                    className="form-control"
                    placeholder="Registered business name"
                    autoComplete="organization"
                    required
                  />
                  <small>As on your GST or company registration documents.</small>
                </div>

                <div className="input-group-wrapper">
                  <label htmlFor="tradeName">Trade Name</label>
                  <input
                    type="text"
                    id="tradeName"
                    value={tradeName}
                    onChange={handleTradeNameChange}
                    className="form-control"
                    placeholder="Name shown to restaurants"
                    autoComplete="organization-title"
                  />
                  <small>Auto-filled from legal name; edit if you use a different brand name.</small>
                </div>

                <div className="input-group-wrapper">
                  <label htmlFor="gstin">GSTIN</label>
                  <input
                    type="text"
                    id="gstin"
                    value={gstin}
                    onChange={handleGstinChange}
                    className="form-control"
                    placeholder="e.g., 29ABCDE1234F1Z5"
                    autoComplete="off"
                    inputMode="text"
                    maxLength={15}
                  />
                  <small>Optional. 15-character GST identification number.</small>
                </div>

                <div className="step-actions step-actions--end">
                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : 'Continue to dashboard'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </AuthPageLayout>
  );
};

export default SupplierSetup;
